import { createContext, useContext, useEffect, useMemo, useState } from 'react'

const CartContext = createContext(null)

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([])
  const [isHydrated, setIsHydrated] = useState(false)

  // Hydrate from localStorage once
  useEffect(() => {
    try {
      const raw = localStorage.getItem('cart_items')
      if (raw) setItems(JSON.parse(raw))
    } catch {}
    setIsHydrated(true)
  }, [])

  // Persist to localStorage only after hydration
  useEffect(() => {
    if (!isHydrated) return
    try {
      localStorage.setItem('cart_items', JSON.stringify(items))
    } catch {}
  }, [items, isHydrated])

  const addToCart = (product, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(p => p.id === product.id)
      if (existing) {
        return prev.map(p => p.id === product.id ? { ...p, quantity: (p.quantity || 1) + quantity } : p)
      }
      return [...prev, { ...product, quantity }]
    })
  }

  const removeFromCart = (productId) => {
    setItems(prev => prev.filter(p => p.id !== productId))
  }

  const clearCart = () => setItems([])

  const setItemQuantity = (productId, quantity) => {
    setItems(prev => {
      if (quantity <= 0) {
        return prev.filter(p => p.id !== productId)
      }
      return prev.map(p => p.id === productId ? { ...p, quantity } : p)
    })
  }

  const totalCount = useMemo(() => items.reduce((sum, p) => sum + (p.quantity || 1), 0), [items])

  const value = { items, addToCart, removeFromCart, clearCart, setItemQuantity, totalCount }
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = () => {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}
